import React from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import pageSizes from './pageSizes';
import OutlinedSelect from '../../OutlinedSelect';
import {Sidebar, SidebarBody, SidebarHead, SidebarProps} from '../Sidebar';
import theme from '../../../../theme';
import trackAnalytics from '../../../../utils/gaia/trackAnalytics';
import Typography from '@material-ui/core/Typography';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import generatePDFMap from './generatePDFMap';

export const defaultPrintProps: MapPrintSidebar = {
    type: 'print',
    scale: 0,
    orientation: 'portrait',
    size: 'letter',
    showCompass: true
};

export interface PrintSidebarProps extends SidebarProps {
    map: mapboxgl.Map | undefined;
    onSidebarChange: (sidebar: MapSidebar) => void;
    sidebar: MapPrintSidebar;
    layers: Layer[] | undefined;
    layerTypes: Record<string, LayerType> | undefined;
}

export default function PrintSidebar(props: PrintSidebarProps) {
    const {onSidebarChange, map, sidebar, layers, layerTypes} = props;
    const jspdfPromise = import('jspdf');

    async function exportPDF() {
        if (!map) return;

        trackAnalytics('Print Map', {
            paper_orientation: sidebar.orientation,
            paper_size: sidebar.size,
            sources: layers ? layers.map(layer => layer.type).sort() : [],
            category: 'map2',
        });

        const glScale: HTMLElement | null = document.querySelector('.mapboxgl-ctrl-scale');

        await generatePDFMap({
            orientation: sidebar.orientation,
            showCompass: sidebar.showCompass,
            size: sidebar.size,
            map,
            attribution: layers && layerTypes && layers.map(l => {
                return [layerTypes[l.type].title, layerTypes[l.type].attribution];
            }) || [],
            scalebar: {
                width: glScale ? glScale.offsetWidth : 0,
                label: glScale ? glScale.innerText : '',
            },
            jspdfPromise,
        });

        onSidebarChange({type: 'hidden'});
    }

    return <Sidebar {...props}>
        <SidebarHead onSidebarChange={onSidebarChange} title="Print" />
        <SidebarBody>
            <div style={{padding: theme.spacing(2)}}>
                <Grid container
                    spacing={1}
                    direction='column'
                    justify='center'
                    alignItems='stretch'
                >
                    <Grid item style={{width: '100%'}}>
                        <OutlinedSelect
                            style={{width: '100%', marginBottom: theme.spacing(1)}}
                            label={'Orientation'}
                            value={sidebar.orientation}
                            onChange={event => {
                                onSidebarChange({
                                    ...sidebar,
                                    orientation: event.target.value as string,
                                });
                            }}
                        >
                            <MenuItem value="portrait">Portrait</MenuItem>
                            <MenuItem value="landscape">Landscape</MenuItem>
                        </OutlinedSelect>
                    </Grid>

                    <Grid item style={{width: '100%'}}>
                        <OutlinedSelect
                            style={{width: '100%', marginBottom: theme.spacing(1)}}
                            label={'Size'}
                            value={sidebar.size}
                            onChange={event => {
                                onSidebarChange({
                                    ...sidebar,
                                    size: event.target.value as MapPrintSidebar['size'],
                                });
                            }}>{Object.entries(pageSizes).map(([value, {width, height}]) =>
                                <MenuItem key={value} value={value}>
                                    <ListItemText
                                        style={{padding: 0, margin: 0}}
                                        primary={value.charAt(0).toUpperCase() + value.slice(1)}
                                        secondary={`${width} x ${height}in`}
                                    />
                                </MenuItem>
                            )}</OutlinedSelect>
                    </Grid>

                    <Grid item xs={12}>
                        <FormGroup style={{
                            marginLeft: 0,
                            marginRight: theme.spacing(2),
                        }}>
                            <FormControlLabel
                                style={{marginLeft: 0, marginTop: theme.spacing(0.5)}}
                                control={<Checkbox
                                    style={{marginRight: 6, padding: 0}}
                                    id="showCompass"
                                    color="primary"
                                    checked={sidebar.showCompass}
                                    onChange={event => {
                                        onSidebarChange({
                                            ...sidebar,
                                            showCompass: event.target.checked,
                                        });
                                    }}
                                />}
                                label={<Typography variant="body1">Show Compass</Typography>}
                            />
                        </FormGroup>
                    </Grid>
                </Grid>
            </div>

            <div style={{display: 'flex'}}>
                <Button
                    onClick={exportPDF}
                    variant='contained'
                    color='primary'
                    style={{
                        color: '#ffffff',
                        backgroundColor: 'primary',
                        marginLeft: theme.spacing(2),
                        marginRight: theme.spacing(2),
                        flex: 1,
                        fontSize: 15,
                    }}>
                    Export PDF
                </Button>
            </div>
        </SidebarBody>
    </Sidebar>;
}
